import { Region, SystemStatus } from './ops'
import { Scene } from './scenes'
import { SourceStream, StreamType } from './streams'
import { Widget } from '@/types/widgets'
import { KeyDict } from '@/types'
import { UserProfile } from '@/types/users'
import { WATERMARK_DEFAULT } from '@/store/types'
import Convertable from '@/classes/Convertable'
import { firestore } from 'firebase'

type Modify<T, R> = Omit<T, keyof R> & R

export interface PlaylistStatus {
  active?: null | string,
  position: number,
  team_id: string
}

export interface JSMemoryConsumption {
  limit: number,
  total: number,
  used: number
}

export interface PlaylistConnection {
  id: string,
  connections: {
    [ip: string]: number
  },
  memory: {
    [ip: string]: JSMemoryConsumption
  },
  volumes: {
    [ip: string]: {
      [streamname: string]: number
    }
  }
}

export interface CastInputStream {
  id: string,
  connected: boolean,
  name: string
}

export interface CastOutputStream {
  ngcvp_status: SystemStatus,
  stream: string
}

export enum CastStatus {
  // TODO: there's also a 'pregame' status - how is this different from "scheduled"?
  Scheduled = 'scheduled',
  InProgress = 'in-progress',
  Archiving = 'archiving',
  Archived = 'archived',
  Unknown = 'unknown',
  Error = 'error',
  OK = 'ok'
}

export enum SupportLevel {
  Silver = '', // for sorting purposes
  Gold = 'gold',
  Platinum = 'platinum',
  Lite = 'lite'
}

export enum EventWizardStep {
  EVENT = 'Event',
  INPUTS = 'Inputs',
  OUTPUTS = 'Outputs',
  CASTERS = 'Casters',
  ASSETS = 'Assets',
  INTEGRATIONS = 'Integrations'
}

export const DEFAULT_SUPPORT_LEVEL = SupportLevel.Gold

export const MIXER_DEFAULT_GOP = 4

export enum CustomRtmpDestinationType {
  URL = 'url',
  Stream = 'stream',
  Output = 'output'
}

export interface CustomRtmpDestination {
  start_time: number,
  streamname: string,
  type: CustomRtmpDestinationType,
  url: string,
  valid: boolean,
  active: boolean,
  gop_size?: number
}

// this output stream is a composite of outputStream and custom stream
// we override the type prop to be inline with other output types
export interface CompositeOutputStream extends Modify<CustomRtmpDestination, {
  type: CustomRtmpDestinationType | StreamType
}>
{
  networkslow?: boolean,
  connected?: boolean,
  ngcvp_status?: SystemStatus,
}

interface ISORecord {
  name: string,
  url: string,
  start: number,
  end: number
}

export interface CastSummary {
  id: string,
  name: string,
  start_date: firebase.firestore.Timestamp
}

export interface CasterRequestCustomAudioMix {
  volumes: KeyDict<number>
  audioMixType: AudioMixType
}

// TODO refactor Tyoescripts Partials (+ any) to full types
export class Cast {
  id: string = ''
  copying: boolean = false // true when being copied
  originalCast: string = '' // JSON serialized original this cast was copied from
  asset_groups: KeyDict<boolean> = {}
  chat_id: string = ''
  create_event: boolean = true
  custom_rtmp_destinations?: KeyDict<CustomRtmpDestination> = {}
  deleted: boolean = false
  anonymous_caster_rules = {
    max_anonymous_casters: 5,
    max_total_casters: 10
  }
  // end_date: DateConstructor = Date
  // end_date: DateConstructor | Date = Date
  end_date: any = new Date() // TODO: Force end_date to be of type Date (and not also EventDate or string)
  event_id: string = ''
  framerate: string = ''
  feedname?: KeyDict<string> = {}
  input_streams: KeyDict<boolean> = {}
  invited_casters: KeyDict<InvitedCaster> = {}
  k360_event_id?: number = 0
  m3u8?: string = ''
  media_upload_url: string = ''
  mp4?: string = ''
  name: string = ''
  isos?: KeyDict<ISORecord> = {}
  ngcvp_status?: SystemStatus = SystemStatus.OK
  online_sessions: KeyDict<string[]> = {}
  output_streams: KeyDict<CastOutputStream> = {}
  presets: string[] = []
  preview_scenes: KeyDict<Scene> = {}
  republishRequested?: KeyDict<boolean>
  quality: string = ''
  region: Region = Region.USEast
  activeScene: string|null = null
  self_muted: KeyDict<boolean> = {}
  scenes: KeyDict<Scene> = {}
  source_streams: KeyDict<Partial<SourceStream>> = {}
  srt: boolean = false
  largemachine: boolean = false
  requestCustomAudioMix?: KeyDict<CasterRequestCustomAudioMix>
  requestSwitchToAudioMix?: KeyDict<AudioMixType>
  // start_date: DateConstructor = Date
  // start_date: DateConstructor | Date = Date
  start_date: any = new Date() // TODO: Force start_date to be of type Date (and not also EventDate or string)
  status?: CastStatus
  bleeped?: KeyDict<number>
  talk_back: KeyDict<boolean> = {}
  team_id: string = ''
  transcodingregion: Region = Region.USEast
  video_start_time?: number = 0
  wrtcserver: string = ''
  widgets: KeyDict<Widget> = {}
  teamGroups: string[] = []
  caster_delay?: number
  template: string = ''
  record_isos?: boolean
  clips?: KeyDict<Clip>
  gop_size: number = 4
  supportLevel: SupportLevel = DEFAULT_SUPPORT_LEVEL
  pendingPlatinumRequest: boolean = false
  platinumApproved: boolean = false
  watermark: boolean = WATERMARK_DEFAULT
  version?: string
  creator_id: string = ''
  token: string = ''
  moderators?: KeyDict<CastModerator> = {}
}

export enum CallTriage {
  Queued = 'CallTriage.Queued',               // 1.  all slots are taken, waiting for a free slot
  Unverified = 'CallTriage.Unverified',       // 2.  got (automatically) assigned to a free slot
  InModeration = 'CallTriage.InModeration',   // 3.  currently videochatting with a moderator
  Rejected = 'CallTriage.Rejected',           // 4a. interviewed by moderator & rejected
  Accepted = 'CallTriage.Accepted',           // 4b. interviewed by moderator & accepted
  Assigned = 'CallTriage.Assigned'            // 4c. interviewed by moderator & assigned to an existing caster
}

export class InvitedCaster extends Convertable {
  userId: string
  anonymous: boolean = false
  moderator: string | null = null
  triage: CallTriage = CallTriage.Accepted
  timestamp: number = Date.now()
  deleted: boolean = false

  constructor (config: Partial<InvitedCaster> = {}) {
    super(config)

    this.userId = config?.userId ?? ''
    this.anonymous = config?.anonymous ?? false
    this.moderator = config?.moderator ?? null
    this.timestamp = config?.timestamp ?? Date.now()
    this.deleted = config?.deleted ?? false

    if (config?.triage) {
      this.triage = config.triage
    } else {
      this.triage = this.anonymous ? CallTriage.Queued : CallTriage.Accepted
    }
  }
}

export interface CastModerator {
  connected: boolean
}

export interface AnonymousCasterSlot {
  currentCasterId: string
  casterHistory: KeyDict<AnonymousCasterSlotHistory>
}

export interface AnonymousCasterSlotHistory {
  casterId: string,
  startTime: number,
  endTime: number
}

export enum AudioMixType {
  Program = 'program',
  LiveEdit = 'liveedit',
  Monitor = 'monitor'
}

export interface Clip {
  start: number
  end: number
  name: string
  url: string,
  deleted?: boolean
}

export interface CastChatMessage {
  '.uid': string,
  message: string,
  timestamp: firestore.Timestamp,
  user_id: string
}

export interface CastTableItem {
  id: string,
  name: string,
  lowercaseName: string,
  startDate: string,
  endDate: string,
  outputs: string,
  k360event: number,
  feedNames: KeyDict<string>,
  casters: UserProfile[],
  hasArchive: boolean,
  archivingText: string,
  archiveExpired: boolean,
  watermark: boolean,
  supportLevel: SupportLevel,
  castInfo: Partial<Cast>
}
